import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import './Charts.css';

const TrendsChart = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode,
  onCompareModeChange
}) => {
  const [selectedChart, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    if (selectedChartOption) {
      if (isCountyOrCity) {
        const prefix = compareMode === 'state' ? stateAbbr : 'US';
        
        if (selectedChartOption.match(/^(US|[A-Z]{2})\s+/) && 
            !selectedChartOption.startsWith(prefix)) {
          const newOption = selectedChartOption.replace(/^(US|[A-Z]{2})\s+/, `${prefix} `);
          setSelectedChart(newOption);
        } else {
          setSelectedChart(selectedChartOption);
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    }
  }, [selectedChartOption, compareMode, isCountyOrCity, stateAbbr]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const hasPreviousCrimeData = [
    'all_crime_count_prev', 'violent_crime_count_prev', 'property_crime_count_prev',
    'murder_count_prev', 'rape_count_prev', 'robbery_count_prev', 'aggravated_assault_count_prev',
    'burglary_count_prev', 'larceny_theft_count_prev', 'motor_vehicle_theft_count_prev'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );

  if (!hasPreviousCrimeData) {
    return (
        <div className="loading">No Previous Data Available</div>
    );
  }

  const handleTouchStart = () => {
    setIsTouching(true);
  };
  
  const handleTouchEnd = () => {
    setIsTouching(false);
  };
  
  const chartOptions = [
    'Total Crime',
    `Previous Total`,
    `Change from Previous Total`,
    `% Change from Previous Total`,
    `Total Crime vs Previous Total`,
    ...((!isNation) ? [
      `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Rank`,
      `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Percentile`
    ] : [])
  ];

  if (!selectedChart || !chartOptions.includes(selectedChart)) {
    return null;
  }

  const getChartOption = (baseOption) => {
    if (baseOption === 'Total Crime') {
      return baseOption;
    }
    
    if (isCountyOrCity) {
      const prefix = compareMode === 'state' ? stateAbbr : 'US';
      return baseOption.replace(`${locationType}`, `${prefix} ${locationType}`);
    }
    
    return baseOption;
  };

  const getTotalLocations = () => {
    if (isCountyOrCity) {
      return compareMode === 'state' ? 
        (isCounty ? current.total_counties_state : current.total_cities_state) || current.total_locations : 
        (isCounty ? current.total_counties_national : current.total_cities_national) || current.total_locations;
    }
    
    return current.total_locations || 0;
  };  

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const getComparisonFields = (prefix) => {
    const rankField = isCountyOrCity
      ? compareMode === 'state'
        ? `${prefix}_state_trend_rank`
        : `${prefix}_national_trend_rank`
      : `${prefix}_trend_rank`;
    
    const percentileField = isCountyOrCity
      ? compareMode === 'state'
        ? `${prefix}_state_trend_percentile`
        : `${prefix}_national_trend_percentile`
      : `${prefix}_trend_percentile`;
    
    return {
      rank: current[rankField],
      rankValue: extractNumber(current[rankField]),
      percentile: (current[percentileField] > 0 && current[percentileField] < 1) ? 1 : current[percentileField]
    };
  };
  
  const transformData = () => {
    const data = selectedSource === 'BJS_NCVS' ? [
      {
        name: 'All Crime',
        currentTotal: current.all_crime_count,
        previousTotal: current.all_crime_count_prev,
        difference: current.all_crime_count_prev_diff,
          percentDiff: current.all_crime_count_prev_diff_pct,
        },
        {
          name: 'Violent Crime',
          currentTotal: current.violent_crime_count,
          previousTotal: current.violent_crime_count_prev,
          difference: current.violent_crime_count_prev_diff,
          percentDiff: current.violent_crime_count_prev_diff_pct,
        },
        {
          name: 'Rape',
          currentTotal: current.rape_count,
          previousTotal: current.rape_count_prev,
          difference: current.rape_count_prev_diff,
          percentDiff: current.rape_count_prev_diff_pct,
        },
        {
          name: 'Robbery',
          currentTotal: current.robbery_count,
          previousTotal: current.robbery_count_prev,
          difference: current.robbery_count_prev_diff,
          percentDiff: current.robbery_count_prev_diff_pct,
        },
        {
          name: 'Aggravated Assault',
          currentTotal: current.aggravated_assault_count,
          previousTotal: current.aggravated_assault_count_prev,
          difference: current.aggravated_assault_count_prev_diff,
          percentDiff: current.aggravated_assault_count_prev_diff_pct,
        },
        {
          name: 'Simple Assault',
          currentTotal: current.simple_assault_count,
          previousTotal: current.simple_assault_count_prev,
          difference: current.simple_assault_count_prev_diff,
          percentDiff: current.simple_assault_count_prev_diff_pct,
        },
        {
          name: 'Property Crime',
          currentTotal: current.property_crime_count,
          previousTotal: current.property_crime_count_prev,
          difference: current.property_crime_count_prev_diff,
          percentDiff: current.property_crime_count_prev_diff_pct,
        },
        {
          name: 'Burglary',
          currentTotal: current.burglary_count,
          previousTotal: current.burglary_count_prev,
          difference: current.burglary_count_prev_diff,
          percentDiff: current.burglary_count_prev_diff_pct,
        },
        {
          name: 'Trespassing',
          currentTotal: current.trespassing_count,
          previousTotal: current.trespassing_count_prev,
          difference: current.trespassing_count_prev_diff,
          percentDiff: current.trespassing_count_prev_diff_pct,
        },
        {
          name: 'Larceny/Theft',
          currentTotal: current.larceny_theft_count,
          previousTotal: current.larceny_theft_count_prev,
          difference: current.larceny_theft_count_prev_diff,
          percentDiff: current.larceny_theft_count_prev_diff_pct,
        },
        {
          name: 'Motor Vehicle Theft',
          currentTotal: current.motor_vehicle_theft_count,
          previousTotal: current.motor_vehicle_theft_count_prev,
          difference: current.motor_vehicle_theft_count_prev_diff,
          percentDiff: current.motor_vehicle_theft_count_prev_diff_pct,
        }
      ] : [
        {
          name: 'All Crime',
          currentTotal: current.all_crime_count,
          previousTotal: current.all_crime_count_prev,
          difference: current.all_crime_count_prev_diff,
          percentDiff: current.all_crime_count_prev_diff_pct,
          ...getComparisonFields('all_crime')
        },
        {
          name: 'Violent Crime',
          currentTotal: current.violent_crime_count,
          previousTotal: current.violent_crime_count_prev,
          difference: current.violent_crime_count_prev_diff,
          percentDiff: current.violent_crime_count_prev_diff_pct,
          ...getComparisonFields('violent_crime')
        },
        {
          name: 'Murder',
          currentTotal: current.murder_count,
          previousTotal: current.murder_count_prev,
          difference: current.murder_count_prev_diff,
          percentDiff: current.murder_count_prev_diff_pct,
          ...getComparisonFields('murder')
        },
        {
          name: 'Rape',
          currentTotal: current.rape_count,
          previousTotal: current.rape_count_prev,
          difference: current.rape_count_prev_diff,
          percentDiff: current.rape_count_prev_diff_pct,
          ...getComparisonFields('rape')
        },
        {
          name: 'Robbery',
          currentTotal: current.robbery_count,
          previousTotal: current.robbery_count_prev,
          difference: current.robbery_count_prev_diff,
          percentDiff: current.robbery_count_prev_diff_pct,
          ...getComparisonFields('robbery')
        },
        {
          name: 'Aggravated Assault',
          currentTotal: current.aggravated_assault_count,
          previousTotal: current.aggravated_assault_count_prev,
          difference: current.aggravated_assault_count_prev_diff,
          percentDiff: current.aggravated_assault_count_prev_diff_pct,
          ...getComparisonFields('aggravated_assault')
        },
        {
          name: 'Property Crime',
          currentTotal: current.property_crime_count,
          previousTotal: current.property_crime_count_prev,
          difference: current.property_crime_count_prev_diff,
          percentDiff: current.property_crime_count_prev_diff_pct,
          ...getComparisonFields('property_crime')
        },
        {
          name: 'Burglary',
          currentTotal: current.burglary_count,
          previousTotal: current.burglary_count_prev,
          difference: current.burglary_count_prev_diff,
          percentDiff: current.burglary_count_prev_diff_pct,
          ...getComparisonFields('burglary')
        },
        {
          name: 'Larceny/Theft',
          currentTotal: current.larceny_theft_count,
          previousTotal: current.larceny_theft_count_prev,
          difference: current.larceny_theft_count_prev_diff,
          percentDiff: current.larceny_theft_count_prev_diff_pct,
          ...getComparisonFields('larceny_theft')
        },
        {
          name: 'Motor Vehicle Theft',
          currentTotal: current.motor_vehicle_theft_count,
          previousTotal: current.motor_vehicle_theft_count_prev,
          difference: current.motor_vehicle_theft_count_prev_diff,
          percentDiff: current.motor_vehicle_theft_count_prev_diff_pct,
          ...getComparisonFields('motor_vehicle_theft')
        }
      ];

      const nonInfinityPctDiffs = data
      .map(item => item.percentDiff)
      .filter(val => val !== 'Infinity' && val !== '-Infinity' && val !== null && val !== undefined);
    
    const maxPctDiff = nonInfinityPctDiffs.length > 0 ? 
      Math.max(...nonInfinityPctDiffs.map(Math.abs)) : 100;
    
    const minPctDiff = nonInfinityPctDiffs.length > 0 ? 
      Math.min(...nonInfinityPctDiffs) : -100;
    
    const infinityPctDiffValue = Math.max(100, maxPctDiff * 2);
    const negativeInfinityPctDiffValue = Math.min(-100, minPctDiff * 2);
  
    const processForChart = (value) => {
      if (value === 'Infinity') return infinityPctDiffValue;
      if (value === '-Infinity') return negativeInfinityPctDiffValue;
      return value;
    };
    
    return data.map(item => ({
      ...item,
      percentDiffForChart: processForChart(item.percentDiff)
    }));
  };

  const chartData = transformData();
  
  const getMaxValue = () => {
    switch (selectedChart) {
      case 'Total Crime':
        return Math.max(...chartData.map(item => item.currentTotal || 0));
      case `Previous Total`:
        return Math.max(...chartData.map(item => item.previousTotal || 0));
      case `Change from Previous Total`:
      case `% Change from Previous Total`:
        const values = chartData.map(item => 
          selectedChart === `Change from Previous Total` ? 
            item.difference : item.percentDiffForChart
        );
        return Math.max(Math.abs(Math.min(...values)), Math.max(...values));
      case getChartOption(`${locationType} Rank`):
        return getTotalLocations();
      case getChartOption(`${locationType} Percentile`):
        return 100;
      default:
        return Math.max(
          ...chartData.map(item => Math.max(item.currentTotal || 0, item.previousTotal || 0))
        );
    }
  };

  const maxValue = getMaxValue();
  
  const getAxisMaxValue = (value) => {
    if (selectedChart === getChartOption(`${locationType} Rank`)) {
      return getTotalLocations();
    } else if (selectedChart === getChartOption(`${locationType} Percentile`)) {
      return 100;
    } else if (selectedChart.includes('%')) {
      return Math.ceil(value / 10) * 10;
    } else {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      const rounded = Math.ceil(normalized / 0.1) * 0.1;
      return rounded * magnitude;
    }
  };
  
  const getTicks = (maxVal) => {
    if (selectedChart === getChartOption(`${locationType} Rank`)) {
      return [1, Math.ceil(getTotalLocations()/4), Math.ceil(getTotalLocations()/2), 
        Math.ceil(getTotalLocations()*3/4), getTotalLocations()];
    } else if (selectedChart === getChartOption(`${locationType} Percentile`)) {
      return [1, 25, 50, 75, 100];
    } else if (selectedChart.includes('Change')) {
      if (isMobile && window.innerWidth < 400) {
        return [-maxVal, 0, maxVal];
      }
      return [-maxVal, -maxVal/2, 0, maxVal/2, maxVal];
    } else {
      if (isMobile && window.innerWidth < 400) {
        return [0, maxVal/2, maxVal];
      }
      return [0, maxVal/4, maxVal/2, maxVal*3/4, maxVal];
    }
  };

  const renderTotalBar = (props) => {
    const { x, y, width, height, payload, dataKey } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const isRank = selectedChart === getChartOption(`${locationType} Rank`);
    const isPercentile = selectedChart === getChartOption(`${locationType} Percentile`);
    const value = props.dataKey === 'currentTotal' ? payload.currentTotal : payload.previousTotal;
    
    const barWidth = Math.max(1, width || 0);
    if (!barWidth) return null;

    if (!isRank && !isPercentile) {  
      if (value === null || value === undefined || isNaN(value)) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              -
            </text>
          </g>
        );
      }
  
      if (value === 0) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              0
            </text>
          </g>
        );
      }
    }
    
    if (isRank || isPercentile) {
      const value = isRank ? payload.rankValue : payload.percentile;     
      const maxValue = isRank ? getTotalLocations() : 100;
      const isNearMax = value >= (maxValue * 0.9);
      const hasValidValue = value !== null && value !== undefined && !isNaN(value);
      
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={barWidth}
            height={height}
            fill="transparent"
          />
          {hasValidValue && (
            <circle
              cx={x + barWidth}
              cy={y + height/2}
              r={6}
              fill={`var(--navy)`}
              fillOpacity={isMainCategory ? 0.6 : 0.3}
              stroke="black"
            />
          )}
          <text
            x={hasValidValue ? x + barWidth + (isNearMax ? -12 : 12) : x + 5}
            y={y + height/2}
            dy="0.35em"
            textAnchor={isNearMax ? "end" : "start"}
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            {isRank ? formatRank(payload.rank) : formatPercentile(payload.percentile)}
          </text>
        </g>
      );
    }
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = (value / maxAxisValue);
    const isComparison = selectedChart === `Total Crime vs Previous Total`;
    const isTextInside = isComparison ? relativePercentage > (isMobile ? 0.5 : 0.25) : relativePercentage > (isMobile ? 0.25 : 0.12);
    const diff = isComparison && dataKey === 'currentTotal' ? payload.currentTotal - payload.previousTotal : null;
    const useComparisonStyle = isComparison && dataKey === 'currentTotal';
    const isPreviousNull = payload.previousTotal === null || payload.previousTotal === undefined || isNaN(payload.previousTotal);
    const color = (isPreviousNull && isComparison)  ? 'silver' : (useComparisonStyle ? getValueColor(diff) : 'navy');
    const chartAreaStart = isMobile ? 160 : 180;
    
    const textX = isTextInside ? 
      Math.max(chartAreaStart + 50, x + barWidth - 4) : 
      Math.max(chartAreaStart + 4, x + barWidth + 4); 
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={barWidth}
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isTextInside ? "end" : "start"}
          fill={isMainCategory && isTextInside ? 
            ((isPreviousNull && isComparison) ? 'black' : 
             useComparisonStyle ? `var(--very-light-${color})` : 'var(--light-gray)') 
            : ((isPreviousNull && isComparison) ? 'black' : `var(--${color})`)}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formatNumber(value)}
          {isComparison && dataKey === 'currentTotal' && (
            <tspan 
              dx={4}
              style={{ fontWeight: 400 }}
            >
              ({formatDifference(payload.difference)})
            </tspan>
          )}
        </text>
      </g>
    );
  };

  const renderDifferenceBar = (props) => {
    const { x, y, width, height, payload } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const value = selectedChart === `Change from Previous Total` ? payload.difference : payload.percentDiff;
    const isNegative = value < 0;
    const color = getValueColor(value);
  
    if (parseFloat(value) === 0) {
      return (
        <g>
          <text
            x={x + 5}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill="var(--color)"
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            {selectedChart === `% Change from Previous Total` ? '0%' : '0'}
          </text>
        </g>
      );
    }

    if (value === null || value === undefined || isNaN(value)) {
      return (
        <g>
          <text
            x={x + 5}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill="var(--color)"
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            -
          </text>
        </g>
      );
    }
    
    const barWidth = Math.abs(width || 0); 
    if (!barWidth) return null; 
    const barX = isNegative ? x + width : x;
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = Math.abs(value / maxAxisValue);
    const isTextInside = relativePercentage > (isMobile ? 0.5 : 0.2);
    
    const textX = isNegative ? 
      (isTextInside ? barX + 4 : barX - 4) : 
      (isTextInside ? x + barWidth - 4 : x + barWidth + 4);
    
    return (
      <g>
        <rect
          x={barX}
          y={y}
          width={barWidth} 
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isNegative ? 
            (isTextInside ? "start" : "end") : 
            (isTextInside ? "end" : "start")}
          fill={isMainCategory && isTextInside ? 
            `var(--very-light-${color})` : 
            `var(--${color})`}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formatDifference(value, selectedChart === `% Change from Previous Total`)}
        </text>
      </g>
    );
  };

  const renderCustomLegend = () => {
    const renderLegendBar = () => {
      switch (selectedChart) {
        case `Change from Previous Total`:
        case `% Change from Previous Total`:
        case `Total Crime vs Previous Total`:
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(0, 128, 0, 0.6), rgba(0, 128, 0, 0.3))',
                borderLeft: '1px solid var(--green)',
                borderTop: '1px solid var(--green)',
                borderBottom: '1px solid var(--green)'
              }} />
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.6))',
                borderRight: '1px solid var(--red)',
                borderTop: '1px solid var(--red)',
                borderBottom: '1px solid var(--red)'
              }} />
            </div>
          );

        case getChartOption(`${locationType} Rank`):
        case getChartOption(`${locationType} Percentile`):
          return (
            <div style={{ 
              width: '12px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)',
              borderRadius: '50%',
            }} />
          );

        default:
          return (
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
          );
      }
    };

    const getDisplayTitle = () => {
      if (selectedSource === 'BJS_NCVS' && selectedChart === 'Total Crime') {
        return 'Total Victimizations';
      }
      if (selectedSource === 'BJS_NCVS') {
        if (selectedChart === `Total Crime vs Previous Total`) {
          return 'Total Victimizations';
        }
      }
      if (selectedChart === `Total Crime vs Previous Total`) {
        return 'Total Crime';
      }
      return selectedChart;
    };

    const getDisplayOption = (option) => {
      if (selectedSource === 'BJS_NCVS' && option === 'Total Crime') {
        return 'Total Victimizations';
      }
      if (selectedSource === 'BJS_NCVS' && option === 'Total Crime vs Previous Total') {
        return 'Total Victimizations vs Previous Total';
      }
      return option;
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendBar()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>{getDisplayTitle()}</span>

            <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
              
            {isMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: getDropdownLeftPosition(selectedChart),
                backgroundColor: 'white',
                border: '1px solid var(--silver)',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: 'max-content',
                marginTop: '4px',
                whiteSpace: 'nowrap'
              }}>
                {chartOptions.map((option, index) => (
                  <div
                    key={option}
                    style={{
                      padding: '8px 12px',
                      cursor: selectedChart === option ? 'not-allowed' : 'pointer',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: 600,
                      textAlign: 'left',
                      backgroundColor: selectedChart === option ? 'var(--light-gray)' : 'transparent',
                      borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      boxSizing: 'border-box',
                      height: '31px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedChart !== option) {
                        e.currentTarget.style.backgroundColor = 'var(--silver)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedChart !== option) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={(e) => {
                      if (selectedChart === option) {
                        e.stopPropagation();
                        return;
                      }
                      e.stopPropagation();
                      setSelectedChart(option);
                      onChartOptionChange(option);
                      setIsMenuOpen(false);
                    }}
                  >
                    <span style={{ opacity: selectedChart === option ? 0.5 : 1 }}>
                      {getDisplayOption(option)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {selectedChart === `Total Crime vs Previous Total` && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>Previous Total</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content" 
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          <BarChart
            key={selectedChart}
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: isMobile ? 30 : 40, left: 20, bottom: 0 }}
          >
            <XAxis 
              type="number"
              tickFormatter={(value, index, ticks) => {
                const hasInfinity = chartData.some(item => 
                  item.percentDiff === 'Infinity' || item.percentDiff === '-Infinity'
                );
            
                if (hasInfinity && selectedChart === `% Change from Previous Total`) {
                    if (index === 0) return '-Inf%';
                    
                    const targetIndex = (isMobile && window.innerWidth < 400) ? 2 : 4;
                    if (index === targetIndex) return '+Inf%';
                }

                if (selectedChart === getChartOption(`${locationType} Percentile`)) {
                  return formatPercentile(value);
                } else if (selectedChart === getChartOption(`${locationType} Rank`)) {
                  return formatRank(value);
                } else if (selectedChart.includes('Change')) {
                  return value === 0 ? '0' : formatDifference(value, selectedChart.includes('%'));
                } else {
                  return value === 0 ? '0' : formatNumber(value);
                }
              }}
              domain={
                selectedChart === getChartOption(`${locationType} Rank`) ? 
                  [1, getTotalLocations()] :
                selectedChart === getChartOption(`${locationType} Percentile`) ?
                  [1, 100] :
                selectedChart.includes('Change') ? 
                  [-getAxisMaxValue(maxValue), getAxisMaxValue(maxValue)] : 
                  [0, getAxisMaxValue(maxValue)]
              }
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={{ stroke: 'var(--border-color)' }}
              ticks={getTicks(getAxisMaxValue(maxValue))}
              tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
              interval="preserveStartEnd"
              allowDecimals={false}
              height={isMobile ? 20 : 30}
            />
            <YAxis 
              type="category" 
              dataKey="name"
              width={isMobile ? 140 : 160}
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={false} 
              interval={0}
              tick={(props) => {
                const { y, payload, index, height, visibleTicksCount } = props;
                let indent = 12;
                if (['Violent Crime', 'Property Crime'].includes(payload.value)) {
                  indent = 24;
                } else if (['Murder', 'Rape', 'Robbery', 'Aggravated Assault', 'Burglary', 'Larceny/Theft', 'Motor Vehicle Theft', 'Simple Assault', 'Trespassing'].includes(payload.value)) {
                  indent = 36;
                }

                const spacing = height / visibleTicksCount;
                const isSelected = payload.value === getCrimeDisplayName(selectedCrime, selectedSource) || 
                  (selectedCrime === 'all_crime' && payload.value === 'All Crime');

                return (
                  <g>
                    {['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.value) && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="var(--light-gray)"
                        fillOpacity={1}
                      />
                    )}
                    {isSelected && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="#fef9c3"
                        fillOpacity={0.9}
                      />
                    )}
                    {index === 0 && (
                      <>
                        <line 
                          x1={0} 
                          y1={y - spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y - spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={4}
                        />
                        <line 
                          x1={0} 
                          y1={y + spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y + spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={2}
                        />
                      </>
                    )}
                    {index > 0 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    {index === chartData.length - 1 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    <text 
                      x={indent} 
                      y={y} 
                      dy={4} 
                      textAnchor="start" 
                      style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
            />
            <CartesianGrid 
              stroke="var(--border-color)"
              strokeDasharray="none"
              horizontal={true}
              horizontalCoordinatesGenerator={({ yAxis }) => {
                return yAxis.scale.domain().map(y => yAxis.scale(y));
              }}
            />
            <line 
              x1={isMobile ? 160 : 180} 
              y1={0} 
              x2="100%" 
              y2={0} 
              stroke="var(--border-color)" 
              strokeWidth={4}
              style={{ transform: `translateX(-${isMobile ? 30 : 40}px)` }}
            />
            <Tooltip 
              cursor={{
                fill: 'rgba(0, 0, 0, 0.1)',
                x: -160,
                width: "calc(100% + 160px)"
              }}
              active={isMobile ? isTouching : undefined}
              wrapperStyle={isMobile ? { transform: 'translateX(100px)' } : {}}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const color = getValueColor(data.difference);
                  
                  const getRowStyle = (type) => ({
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: '24px',
                    backgroundColor: (
                      (selectedChart === 'Total Crime' && type === 'Total Crime') ||
                      (selectedChart === `Previous Total` && type === 'Previous Total') ||
                      (selectedChart === `Change from Previous Total` && type === 'Change from Previous Total') ||
                      (selectedChart === `% Change from Previous Total` && type === '% Change from Previous Total') ||
                      (selectedChart === getChartOption(`${locationType} Rank`) && type === 'Rank') ||
                      (selectedChart === getChartOption(`${locationType} Percentile`) && type === 'Percentile') ||
                      (selectedChart === `Total Crime vs Previous Total` && 
                        (type === 'Total Crime' || type === 'Previous Total' || type === 'Change from Previous Total'))
                    ) ? '#fef9c3' : 'transparent',
                    padding: '2px 4px',
                    margin: '0 -4px'
                  });
                  
                  return (
                    <div className="custom-tooltip" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '12px',
                      border: '1px solid var(--silver)',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 600,
                        fontSize: isMobile ? '13px' : '14px',
                        color: 'black',
                        textAlign: 'left',
                        paddingBottom: '2px',
                      }}>
                        {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                      </div>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 400,
                        fontSize: isMobile ? '13px' : '14px',
                        textAlign: 'left',
                        borderBottom: '1px solid var(--silver)',
                        paddingBottom: '8px'
                      }}>
                        {selectedDateRange}
                      </div>
                      <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px', paddingTop: '6px' }}>
                        <div style={getRowStyle('Total Crime')}>
                          <span style={{ fontWeight: 600 }}>
                            Total {data.name === 'All Crime' 
                              ? (selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime') 
                              : data.name}:
                          </span>
                          <span>{formatNumber(data.currentTotal)}</span>
                        </div>
                        <div style={getRowStyle('Previous Total')}>
                          <span style={{ fontWeight: 600 }}>Previous Total:</span>
                          <span>{formatNumber(data.previousTotal)}</span>
                        </div>
                        <div style={getRowStyle('Change from Previous Total')}>
                          <span style={{ fontWeight: 600 }}>Change from Previous Total:</span>
                          <span style={{ color: `var(--${color})` }}>{formatDifference(data.difference)}</span>
                        </div>
                        <div style={getRowStyle('% Change from Previous Total')}>
                          <span style={{ fontWeight: 600 }}>% Change from Previous Total:</span>
                          <span style={{ color: `var(--${color})`, fontWeight: 600 }}>{formatDifference(data.percentDiff, true)}</span>
                        </div>
                        {!isNation && (
                          <>
                            <div style={getRowStyle('Rank')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Rank:
                              </span>
                              <span>{formatRank(data.rank)}</span>
                            </div>
                            <div style={getRowStyle('Percentile')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Percentile:
                              </span>
                              <span>{formatPercentile(data.percentile)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            {selectedChart === `Total Crime vs Previous Total` ? (
              <>
                <Bar dataKey="currentTotal" shape={renderTotalBar} />
                <Bar dataKey="previousTotal" shape={renderTotalBar} />
              </>
            ) : (
              <Bar
                dataKey={
                  selectedChart === 'Total Crime' ? 'currentTotal' :
                  selectedChart === 'Previous Total' ? 'previousTotal' :
                  selectedChart === getChartOption(`${locationType} Rank`) ? 'rankValue' :
                  selectedChart === getChartOption(`${locationType} Percentile`) ? 'percentile' :
                  selectedChart === `Change from Previous Total` ? 'difference' : 'percentDiffForChart'
                }
                shape={selectedChart.includes('Change') ? renderDifferenceBar : renderTotalBar}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendsChart;